import { useState } from 'react'
import { useTranslation } from '../../i18n/LanguageContext'
import { useSessionMode } from '../../professional/SessionModeContext'
import { useNumTarget, type Difficulty, type Operator, type NumTargetState } from './useNumTarget'
import { useGameCompletion } from '../../hooks/useGameCompletion'
import { useTimer } from '../useTimer'
import { formatGameTime } from '../utils'
import GameSetupScreen from '../GameSetupScreen'
import GameCompleteScreen from '../GameCompleteScreen'
import GamePlayHeader from '../GamePlayHeader'
import SaveErrorBanner from '../SaveErrorBanner'
import { COLORS, SHADOWS, RADIUS, FONT } from '../../styles/tokens'
import { useMobile } from '../../hooks/useMobile'

type GameScreen = 'setup' | 'playing' | 'complete'

const DIFFICULTIES: Difficulty[] = ['easy', 'medium', 'hard']

export default function NumTargetGame({ onBack }: { onBack: () => void }) {
  const { t }       = useTranslation()
  const g           = t.numtarget
  const sessionMode = useSessionMode()

  const [screen,     setScreen]     = useState<GameScreen>(sessionMode ? 'playing' : 'setup')
  const [difficulty, setDifficulty] = useState<Difficulty>(
    (sessionMode?.config.difficulty as Difficulty) ?? 'easy',
  )

  const {
    state, allowedOps, allowParens, totalRounds,
    resetGame, tapCard, tapOperator, tapParen,
    deleteLast, clearExpression, submitAnswer,
  } = useNumTarget(difficulty)

  const { gameComplete, moves, startTime, endTime, roundResults, puzzles } = state
  const totalCardsAcrossRounds = puzzles.reduce((sum, p) => sum + p.cards.length, 0)
  const totalCardsUsed         = roundResults.reduce((sum, r) => sum + r.cardsUsed, 0)

  const { saveError, clearSaveError } = useGameCompletion({
    gameComplete,
    activitySlug:  'numtarget',
    difficulty,
    moves,
    startTime,
    endTime,
    pairsTotal:   totalCardsAcrossRounds,
    pairsMatched: totalCardsUsed,
    onCompleted:  () => setScreen('complete'),
  })

  const handleStart = () => {
    resetGame()
    setScreen('playing')
  }

  return (
    <div style={s.root}>
      {saveError && (
        <SaveErrorBanner
          message={t.common.saveError}
          dismissLabel={t.common.dismissError}
          onDismiss={clearSaveError}
        />
      )}
      {screen === 'setup' && (
        <SetupScreen
          difficulty={difficulty}
          onDifficultyChange={setDifficulty}
          onStart={handleStart}
          onBack={onBack}
        />
      )}
      {screen === 'playing' && (
        <PlayScreen
          state={state}
          allowedOps={allowedOps}
          allowParens={allowParens}
          totalRounds={totalRounds}
          onTapCard={tapCard}
          onTapOperator={tapOperator}
          onTapParen={tapParen}
          onDeleteLast={deleteLast}
          onClear={clearExpression}
          onSubmit={submitAnswer}
          onBack={() => setScreen('setup')}
        />
      )}
      {screen === 'complete' && (
        <GameCompleteScreen
          emoji="🎯"
          title={g.complete.title}
          subtitle={g.complete.subtitle}
          stats={[
            { value: `${totalCardsUsed}/${totalCardsAcrossRounds}`, label: g.complete.cardsUsedLabel },
            { value: formatGameTime(startTime, endTime),             label: g.complete.timeLabel },
            { value: String(moves),                                  label: g.complete.attemptsLabel },
            { value: g.difficulty[difficulty].label,                 label: g.complete.levelLabel },
          ]}
          playAgainLabel={g.complete.playAgain}
          changeLevelLabel={g.complete.changeLevel}
          backLabel={g.complete.backToDashboard}
          onPlayAgain={handleStart}
          onChangeLevel={() => setScreen('setup')}
          onBack={onBack}
        />
      )}
    </div>
  )
}

/* ── Setup screen ───────────────────────────────────────────── */

function SetupScreen({
  difficulty, onDifficultyChange, onStart, onBack,
}: {
  difficulty: Difficulty
  onDifficultyChange: (d: Difficulty) => void
  onStart: () => void
  onBack: () => void
}) {
  const { t } = useTranslation()
  const g     = t.numtarget

  const difficulties = DIFFICULTIES.map((d) => ({
    id:    d,
    label: g.difficulty[d].label,
    hint:  g.difficulty[d].hint,
  }))

  return (
    <GameSetupScreen
      title={g.title}
      subtitle={g.subtitle}
      backLabel={g.backToDashboard}
      onBack={onBack}
      startLabel={g.startBtn}
      onStart={onStart}
      difficulties={difficulties}
      selectedDifficulty={difficulty}
      onDifficultyChange={(d) => onDifficultyChange(d as Difficulty)}
      chooseDifficultyLabel={g.chooseDifficultyTitle}
      preview={<GamePreview />}
    />
  )
}

function GamePreview() {
  const { t } = useTranslation()
  const g     = t.numtarget
  return (
    <div style={s.preview}>
      <div style={s.previewGoalBox}>
        <span style={s.previewGoalLabel}>{g.goalLabel}</span>
        <span style={s.previewGoalNum}>12</span>
      </div>
      <div style={s.previewCards}>
        {[4, 3, 1, 2].map((v, i) => (
          <div key={i} style={{ ...s.previewCard, opacity: i < 2 ? 0.4 : 1 }}>
            {v}
          </div>
        ))}
      </div>
      <div style={s.previewExpr}>
        <span style={s.previewToken}>4</span>
        <span style={{ ...s.previewToken, ...s.previewOp }}>×</span>
        <span style={s.previewToken}>3</span>
        <span style={{ ...s.previewToken, ...s.previewOp }}>=</span>
        <span style={{ ...s.previewToken, color: COLORS.greenDark }}>12 ✓</span>
      </div>
    </div>
  )
}

/* ── Play screen ────────────────────────────────────────────── */

function PlayScreen({
  state, allowedOps, allowParens, totalRounds,
  onTapCard, onTapOperator, onTapParen, onDeleteLast, onClear, onSubmit, onBack,
}: {
  state:          NumTargetState
  allowedOps:     Operator[]
  allowParens:    boolean
  totalRounds:    number
  onTapCard:      (id: number) => void
  onTapOperator:  (op: Operator) => void
  onTapParen:     (which: 'open' | 'close') => void
  onDeleteLast:   () => void
  onClear:        () => void
  onSubmit:       () => void
  onBack:         () => void
}) {
  const { t } = useTranslation()
  const g     = t.numtarget
  const isMobile = useMobile()
  const timer = useTimer(state.startTime, state.endTime)

  const locked        = state.correctFlash
  const lastExprToken = state.expression[state.expression.length - 1]
  const canAddNum    = !locked && (state.expression.length === 0 || lastExprToken?.type === 'operator' || lastExprToken?.type === 'paren-open')
  const canAddOp     = !locked && (lastExprToken?.type === 'number' || lastExprToken?.type === 'paren-close')
  const canOpenParen = !locked && canAddNum
  const canCloseParen = !locked && state.openParens > 0 && (lastExprToken?.type === 'number' || lastExprToken?.type === 'paren-close')
  const canSubmit    = !locked && state.openParens === 0 && (lastExprToken?.type === 'number' || lastExprToken?.type === 'paren-close') && state.expression.length > 0

  const resultOk = state.currentResult === state.goal
  const progress = Math.round(((state.currentRound + (state.correctFlash ? 1 : 0)) / totalRounds) * 100)

  const opLabel = (op: Operator) => {
    if (op === '+') return g.ops.add
    if (op === '-') return g.ops.sub
    if (op === '×') return g.ops.mul
    if (op === '÷') return g.ops.div
    return op
  }

  return (
    <div style={s.playRoot}>
      <GamePlayHeader
        title={g.title}
        subtitle={g.roundCounter
          .replace('{n}', String(state.currentRound + 1))
          .replace('{total}', String(totalRounds))}
        stats={[
          { value: timer,       label: g.complete.timeLabel },
          { value: state.moves, label: g.attemptsLabel },
        ]}
        progress={progress}
        progressNow={state.currentRound}
        progressMax={totalRounds}
        onBack={onBack}
        backLabel={g.backToDashboard}
      />

      <div style={{ ...s.playContent, paddingTop: isMobile ? '100px' : '108px' }}>
        {/* Goal */}
        <div style={{
          ...s.goalSection,
          background:   state.correctFlash ? COLORS.greenSurface : 'transparent',
          borderRadius: RADIUS.lg,
          padding:      state.correctFlash ? '12px 24px' : '0',
          border:       state.correctFlash ? `2px solid ${COLORS.greenLight}` : '2px solid transparent',
          transition:   'background 0.2s, border-color 0.2s',
        }}>
          <span style={s.goalLabel}>{g.goalLabel}</span>
          <span style={{ ...s.goalNumber, color: state.correctFlash ? COLORS.greenLight : COLORS.greenDark }}>
            {state.goal} {state.correctFlash ? '✓' : ''}
          </span>
        </div>

        {/* Expression area */}
        <div style={s.exprArea}>
          <div style={s.exprTokens}>
            {state.expression.length === 0 ? (
              <span style={s.exprPlaceholder}>{g.instruction}</span>
            ) : (
              state.expression.map((tok, i) =>
                tok.type === 'number'
                  ? <span key={i} style={s.exprNumToken}>{tok.value}</span>
                  : tok.type === 'operator'
                  ? <span key={i} style={s.exprOpToken}>{opLabel(tok.op)}</span>
                  : <span key={i} style={s.exprParenToken}>{tok.type === 'paren-open' ? '(' : ')'}</span>
              )
            )}
          </div>

          {state.expression.length > 0 && (
            <div style={s.exprResult}>
              <span style={s.resultLabelText}>{g.resultLabel}</span>
              <span style={{
                ...s.resultValue,
                color: resultOk ? COLORS.greenLight : state.currentResult !== null ? COLORS.textBody : COLORS.textMuted,
              }}>
                {state.currentResult !== null ? state.currentResult : '—'}
                {resultOk && ' ✓'}
              </span>
            </div>
          )}
        </div>

        {/* Operator buttons */}
        <div style={s.opsRow}>
          {allowedOps.map((op) => (
            <button
              key={op}
              style={{
                ...s.opBtn,
                opacity:    canAddOp ? 1 : 0.35,
                background: canAddOp ? COLORS.green : '#E8E0D8',
                color:      canAddOp ? '#fff' : COLORS.textMuted,
              }}
              onClick={() => onTapOperator(op)}
              disabled={!canAddOp}
              aria-label={opLabel(op)}
            >
              {opLabel(op)}
            </button>
          ))}
          {allowParens && (
            <>
              <button
                style={{
                  ...s.opBtn, ...s.parenBtn,
                  opacity:    canOpenParen ? 1 : 0.35,
                  color:      canOpenParen ? COLORS.greenDark : COLORS.textMuted,
                }}
                onClick={() => onTapParen('open')}
                disabled={!canOpenParen}
                aria-label={g.ops.parenOpen}
              >
                {g.ops.parenOpen}
              </button>
              <button
                style={{
                  ...s.opBtn, ...s.parenBtn,
                  opacity:    canCloseParen ? 1 : 0.35,
                  color:      canCloseParen ? COLORS.greenDark : COLORS.textMuted,
                }}
                onClick={() => onTapParen('close')}
                disabled={!canCloseParen}
                aria-label={g.ops.parenClose}
              >
                {g.ops.parenClose}
              </button>
            </>
          )}
          <button
            style={{ ...s.opBtn, ...s.deleteBtn }}
            onClick={onDeleteLast}
            disabled={state.expression.length === 0}
            aria-label={g.deleteBtn}
          >
            {g.deleteBtn}
          </button>
        </div>

        {/* Number cards */}
        <div style={s.cardsGrid}>
          {state.cards.map((card) => (
            <button
              key={card.id}
              style={{
                ...s.cardBtn,
                opacity:         card.inExpression ? 0.3 : canAddNum ? 1 : 0.45,
                background:      card.inExpression ? '#D0E8D8' : COLORS.bgCard,
                border:          card.inExpression
                  ? `2px solid ${COLORS.greenLight}`
                  : `2px solid ${COLORS.borderWarm}`,
                transform:       card.inExpression ? 'scale(0.95)' : 'scale(1)',
                cursor:          card.inExpression || !canAddNum ? 'default' : 'pointer',
              }}
              onClick={() => onTapCard(card.id)}
              disabled={card.inExpression || !canAddNum}
              aria-label={String(card.value)}
              aria-pressed={card.inExpression}
            >
              {card.value}
            </button>
          ))}
        </div>

        {/* Action row */}
        <div style={s.actionRow}>
          <button
            style={{ ...s.clearBtn }}
            onClick={onClear}
            disabled={state.expression.length === 0}
          >
            {g.clearBtn}
          </button>
          <button
            style={{
              ...s.submitBtn,
              ...(state.wrongSubmit ? s.submitBtnWrong : {}),
              opacity: canSubmit ? 1 : 0.45,
            }}
            onClick={onSubmit}
            disabled={!canSubmit}
            className={state.wrongSubmit ? 'wm-shake' : undefined}
          >
            {g.submitBtn}
          </button>
        </div>

        {/* Instruction */}
        <p style={s.instruction}>
          {canAddNum
            ? g.instruction
            : canAddOp
            ? `${g.goalLabel}: ${state.goal}`
            : ''}
        </p>
      </div>
    </div>
  )
}

/* ── Styles ─────────────────────────────────────────────────── */

const s: Record<string, React.CSSProperties> = {
  root: {
    minHeight:  '100vh',
    background: COLORS.bgPage,
  },

  // ── Preview
  preview: {
    display:       'flex',
    flexDirection: 'column',
    alignItems:    'center',
    gap:           12,
    padding:       '16px 8px',
  },
  previewGoalBox: {
    display:        'flex',
    flexDirection:  'column',
    alignItems:     'center',
    background:     COLORS.greenSurface,
    borderRadius:   RADIUS.md,
    padding:        '8px 24px',
    border:         `1.5px solid ${COLORS.greenPale}`,
  },
  previewGoalLabel: {
    fontSize: FONT.size.sm,
    color:    COLORS.textSecondary,
  },
  previewGoalNum: {
    fontSize:   '2rem',
    fontWeight: 700,
    color:      COLORS.greenDark,
    lineHeight: 1,
  },
  previewCards: {
    display: 'flex',
    gap:     8,
  },
  previewCard: {
    width:          44,
    height:         44,
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    background:     COLORS.bgCard,
    border:         `2px solid ${COLORS.borderWarm}`,
    borderRadius:   RADIUS.md,
    fontSize:       FONT.size.xl,
    fontWeight:     700,
    color:          COLORS.greenDark,
    boxShadow:      SHADOWS.subtle,
  },
  previewExpr: {
    display:    'flex',
    alignItems: 'center',
    gap:        8,
    fontSize:   FONT.size.lg,
    fontWeight: 600,
  },
  previewToken: {
    color:      COLORS.textBody,
    fontWeight: 700,
  },
  previewOp: {
    color: COLORS.green,
  },

  // ── Play root
  playRoot: {
    minHeight:  '100vh',
    background: COLORS.bgPage,
  },
  playContent: {
    display:       'flex',
    flexDirection: 'column',
    alignItems:    'center',
    gap:           20,
    padding:       '0 16px 40px',
  },

  // ── Goal
  goalSection: {
    display:       'flex',
    flexDirection: 'column',
    alignItems:    'center',
    gap:           4,
    marginTop:     24,
  },
  goalLabel: {
    fontSize:      FONT.size.md,
    color:         COLORS.textSecondary,
    fontWeight:    600,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },
  goalNumber: {
    fontSize:   '4rem',
    fontWeight: 800,
    color:      COLORS.greenDark,
    lineHeight: 1,
    fontFamily: 'inherit',
  },

  // ── Expression
  exprArea: {
    width:        '100%',
    maxWidth:     480,
    background:   COLORS.bgCard,
    borderRadius: RADIUS.lg,
    border:       `1.5px solid ${COLORS.borderWarm}`,
    padding:      '16px 20px',
    boxShadow:    SHADOWS.subtle,
    minHeight:    80,
  },
  exprTokens: {
    display:   'flex',
    flexWrap:  'wrap',
    gap:       8,
    alignItems:'center',
    minHeight: 44,
  },
  exprPlaceholder: {
    color:     COLORS.textMuted,
    fontSize:  FONT.size.base,
    fontStyle: 'italic',
  },
  exprNumToken: {
    display:        'inline-flex',
    alignItems:     'center',
    justifyContent: 'center',
    minWidth:        40,
    minHeight:       40,
    padding:         '0 10px',
    background:      COLORS.greenSurface,
    border:          `1.5px solid ${COLORS.greenPale}`,
    borderRadius:    RADIUS.sm,
    fontSize:        FONT.size.xl,
    fontWeight:      700,
    color:           COLORS.greenDark,
  },
  exprOpToken: {
    display:        'inline-flex',
    alignItems:     'center',
    justifyContent: 'center',
    minWidth:        32,
    minHeight:       40,
    fontSize:        FONT.size.xl,
    fontWeight:      700,
    color:           COLORS.green,
  },
  exprParenToken: {
    display:        'inline-flex',
    alignItems:     'center',
    justifyContent: 'center',
    minWidth:        24,
    minHeight:       40,
    fontSize:        FONT.size.xl,
    fontWeight:      700,
    color:           COLORS.textSecondary,
  },
  exprResult: {
    display:        'flex',
    alignItems:     'center',
    gap:            8,
    marginTop:      12,
    paddingTop:     10,
    borderTop:      `1px solid ${COLORS.borderWarm}`,
  },
  resultLabelText: {
    fontSize:   FONT.size.sm,
    color:      COLORS.textSecondary,
    fontWeight: 600,
  },
  resultValue: {
    fontSize:   FONT.size.xl,
    fontWeight: 700,
  },

  // ── Operators
  opsRow: {
    display:   'flex',
    gap:       10,
    flexWrap:  'wrap',
    justifyContent: 'center',
    width:     '100%',
    maxWidth:  480,
  },
  opBtn: {
    minWidth:       56,
    minHeight:      56,
    borderRadius:   RADIUS.md,
    border:         'none',
    fontSize:       FONT.size.xl,
    fontWeight:     700,
    cursor:         'pointer',
    boxShadow:      SHADOWS.subtle,
    transition:     'opacity 0.15s, transform 0.1s',
  },
  deleteBtn: {
    background: COLORS.errorBg,
    color:      COLORS.errorText,
    fontSize:   FONT.size.lg,
    minWidth:   56,
  },
  parenBtn: {
    background: COLORS.bgCard,
    border:     `1.5px solid ${COLORS.borderWarm}`,
    fontSize:   FONT.size.xl,
    fontWeight: 700,
    minWidth:   48,
  },

  // ── Cards grid
  cardsGrid: {
    display:             'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap:                 12,
    width:               '100%',
    maxWidth:            480,
  },
  cardBtn: {
    minHeight:      72,
    borderRadius:   RADIUS.card,
    border:         'none',
    fontSize:       FONT.size.h2,
    fontWeight:     800,
    color:          COLORS.greenDark,
    cursor:         'pointer',
    boxShadow:      SHADOWS.subtle,
    transition:     'opacity 0.15s, transform 0.15s, background 0.15s',
  },

  // ── Action row
  actionRow: {
    display:        'flex',
    gap:            12,
    width:          '100%',
    maxWidth:       480,
  },
  clearBtn: {
    flex:           1,
    minHeight:      52,
    borderRadius:   RADIUS.md,
    border:         `1.5px solid ${COLORS.borderWarm}`,
    background:     COLORS.bgCard,
    color:          COLORS.textBody,
    fontSize:       FONT.size.base,
    fontWeight:     600,
    cursor:         'pointer',
  },
  submitBtn: {
    flex:           2,
    minHeight:      52,
    borderRadius:   RADIUS.md,
    border:         'none',
    background:     COLORS.gradientPrimary,
    color:          '#fff',
    fontSize:       FONT.size.lg,
    fontWeight:     700,
    cursor:         'pointer',
    boxShadow:      SHADOWS.button,
    transition:     'opacity 0.15s',
  },
  submitBtnWrong: {
    background: `linear-gradient(135deg, ${COLORS.errorText}, #E85555)`,
  },

  // ── Instruction
  instruction: {
    fontSize:   FONT.size.base,
    color:      COLORS.textSecondary,
    fontStyle:  'italic',
    textAlign:  'center',
    margin:     0,
  },
}
